// src/services/api.js - גרסה מלאה ומאופטמת

import cacheService from './cache';

const API_CONFIG = {
  baseUrl: 'https://us-central1-savings-comparison-system.cloudfunctions.net',
  timeout: 10000
};

class OptimizedLocalAPI {
  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
    this.cache = cacheService;
    this.pendingRequests = new Map();
    this.localData = new Map(); // cache מקומי לנתונים שמורים
    
    // 🆕 Cache מקומי לפי קטגוריה עם TTL
    this.categoryCache = new Map();
    this.categoryTTL = 10 * 60 * 1000; // 10 דקות
    
    // 🚀 Cache ספציפי לטבלאות קטנות - חדש!
    this.tableCache = new Map();
    this.tableTTL = 15 * 60 * 1000; // 15 דקות
  }

  /**
   * 📊 טעינת נתונים מהאחסון המקומי (Firebase Storage) - ללא שינוי
   */
  async loadStoredData(source) {
    try {
      const cacheKey = `stored_data_${source}`;
      
      // בדיקה אם יש בcache המקומי
      if (this.localData.has(cacheKey)) {
        return this.localData.get(cacheKey);
      }
      
      // 🚀 Deduplicate - אם יש בקשת טעינה מתמשכת, תחכה לרכיב משותף
      if (this.pendingRequests.has(cacheKey)) {
        return await this.pendingRequests.get(cacheKey);
      }
      
      // צור promise חדש ושיתוף אותו
      const fetchPromise = (async () => {
        const response = await fetch(`${this.baseUrl}/getStorageData?source=${source}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          timeout: API_CONFIG.timeout
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'שגיאה בטעינת נתונים');
        }

        // שמירה בcache המקומי
        this.localData.set(cacheKey, result.data);
        
        return result.data;
      })();
      
      // שמור את ה-promise
      this.pendingRequests.set(cacheKey, fetchPromise);
      
      try {
        return await fetchPromise;
      } finally {
        // נקה את ה-pending request
        this.pendingRequests.delete(cacheKey);
      }

    } catch (error) {
      // נקה את ה-pending request במקרה של שגיאה
      this.pendingRequests.delete(`stored_data_${source}`);
      throw new Error(`שגיאה בטעינת נתונים מ-${source}: ${error.message}`);
    }
  }

  /**
   * 🚀 טעינה מקבילה של כל המקורות - מעודכן
   */
  async loadAllStoredData() {
    try {
     
      
      const sources = ['gamal-net', 'pension-net', 'insurance-net'];
      
      // 🔥 טעינה מקבילה במקום סדרתית!
      const loadPromises = sources.map(async (source) => {
        try {
          const sourceData = await this.loadStoredData(source);
          return {
            source,
            data: sourceData.map(item => ({
              ...item,
              _source: source,
              _sourceLabel: this.getSourceLabel(source)
            }))
          };
        } catch (error) {
          console.warn(`⚠️ Could not load ${source}:`, error.message);
          return { source, data: [] };
        }
      });

      const results = await Promise.all(loadPromises);
      const allData = results.flatMap(result => result.data);
      

      return allData;

    } catch (error) {
      console.error('❌ Error loading all stored data:', error);
      throw error;
    }
  }

  /**
   * 🎯 טעינה מהירה ספציפית לקטגוריה - חדש
   */
  async getProductsByCategory(categoryId, options = {}) {
    const startTime = performance.now();
    
    try {
      // בדיקת cache מקומי עם TTL
      const cacheKey = `category_${categoryId}`;
      const cached = this.categoryCache.get(cacheKey);
      
      if (cached && (Date.now() - cached.timestamp) < this.categoryTTL) {
     
        return this.processStoredProducts(cached.data, options);
      }

    
      // מיפוי חכם לפי קטגוריה
      const sourceMapping = this.getCategoryToSourceMapping(categoryId);
      let categoryData;

      if (sourceMapping) {
        // טעינה ממקור ספציפי בלבד
        const rawData = await this.loadStoredData(sourceMapping);
        const annotatedData = rawData.map(item => ({
          ...item,
          _source: sourceMapping,
          _sourceLabel: this.getSourceLabel(sourceMapping)
        }));
        console.debug(`AI score source annotation`, {
          categoryId,
          source: sourceMapping,
          sampleId: annotatedData[0]?.id,
          sampleSource: annotatedData[0]?._source,
          sampleLabel: annotatedData[0]?._sourceLabel
        });
        categoryData = this.filterByCategory(annotatedData, categoryId);
      } else {
        // טעינה מכל המקורות רק אם צריך
       
        const allData = await this.loadAllStoredData();
        categoryData = this.filterByCategory(allData, categoryId);
      }

      // שמירה ב-cache מקומי
      this.categoryCache.set(cacheKey, {
        data: categoryData,
        timestamp: Date.now()
      });

      const duration = Math.round(performance.now() - startTime);
     
      return this.processStoredProducts(categoryData, options);

    } catch (error) {
      console.error(`❌ Error loading category ${categoryId}:`, error);
      throw error;
    }
  }
// הוסף פונקציה חדשה לפני getProductsByCategory
async preloadCategory(categoryId) {
  try {
    console.log(`🔄 Preloading category: ${categoryId}`);
    const startTime = performance.now();
    
    await this.getProductsByCategory(categoryId, { limit: 200 });
    
    const duration = Math.round(performance.now() - startTime);
    console.log(`✅ Category preloaded: ${categoryId} (${duration}ms)`);
  } catch (error) {
    console.warn(`⚠️ Preload failed for ${categoryId}:`, error);
  }
}

  /**
   * 🎯 קבלת מוצרים ספציפיים לפי IDs - חדש
   */
// בפונקציה getProductsByIds
async getProductsByIds(fundIds, categoryId) {
  const startTime = performance.now();
  
  try {
    if (!fundIds || fundIds.length === 0) return [];

    // 🆕 1. בדוק cache טבלה
    const tableKey = this.cache.createKey('table', {
      category: categoryId,
      fundIds: fundIds.sort((a,b) => a-b).join(',')
    });
    
    const cached = this.cache.get(tableKey);
    if (cached) {
      
      return cached;
    }

    // 🆕 2. קודם נסה למצוא ב-cache קטגוריה
    const categoryKey = this.cache.createKey('category', {
      category: categoryId,
      limit: 'all',
      sortBy: 'default'
    });
    
    let categoryData = this.cache.get(categoryKey);
    
    // 🚀 3. Deduplicate - אם יש בקשת טעינה מתמשכת לקטגוריה, תחכה לרכיב משותף
    if (!categoryData) {
      const pendingKey = `category_${categoryId}`;
      
      if (this.pendingRequests.has(pendingKey)) {
      
        categoryData = await this.pendingRequests.get(pendingKey);
      } else {
        // צור promise חדש ושיתוף אותו
        const categoryPromise = this.getProductsByCategory(categoryId, { limit: 2000 });
        this.pendingRequests.set(pendingKey, categoryPromise);
        
        try {
          categoryData = await categoryPromise;
        } finally {
          // נקה את ה-pending request
          this.pendingRequests.delete(pendingKey);
        }
      }
    } else {
      console.log(`✅ Using cached category: ${categoryId}`);
    }
    
    // 4. סנן לפי IDs - שיפור השוואת IDs
    const targetIds = new Set(fundIds.map(id => {
      const numId = parseInt(id);
      return isNaN(numId) ? String(id) : numId;
    }));
    
    const results = categoryData.filter(product => {
      const productId = product.id;
      const numId = parseInt(productId);
      const idToCheck = isNaN(numId) ? String(productId) : numId;
      return targetIds.has(idToCheck);
    });

    const orderedResults = fundIds
      .map(id => {
        const numId = parseInt(id);
        const idToFind = isNaN(numId) ? String(id) : numId;
        return results.find(product => {
          const productId = product.id;
          const productNumId = parseInt(productId);
          const productIdToCheck = isNaN(productNumId) ? String(productId) : productNumId;
          return productIdToCheck === idToFind;
        });
      })
      .filter(Boolean);

    // 5. שמור ב-cache טבלה
    this.cache.set(tableKey, orderedResults, this.cache.CACHE_DURATION.TABLE);

    const duration = Math.round(performance.now() - startTime);
   
    
    return orderedResults;

  } catch (error) {
    console.error(`❌ Error getting products by IDs:`, error);
    return [];
  }
}



  /**
   * 🏥 בדיקת בריאות API - ללא שינוי
   */
  async healthCheck() {
    try {
      const cacheKey = this.cache.createKey('health', 'check');
      const cached = this.cache.get(cacheKey);
      if (cached) return cached;

      // בדיקה מהנתונים השמורים
      const sources = ['gamal-net', 'pension-net', 'insurance-net'];
      const sourceStatus = {};
      let totalRecords = 0;

      for (const source of sources) {
        try {
          const data = await this.loadStoredData(source);
          sourceStatus[source] = {
            status: 'healthy',
            records: data.length
          };
          totalRecords += data.length;
        } catch (error) {
          sourceStatus[source] = {
            status: 'error',
            error: error.message,
            records: 0
          };
        }
      }

      const healthResult = {
        status: 'healthy',
        message: 'נתונים שמורים זמינים',
        totalRecords: totalRecords,
        sources: sourceStatus,
        lastUpdate: new Date().toISOString(),
        dataSource: 'stored'
      };

      // Cache ל-5 דקות
      this.cache.set(cacheKey, healthResult, 5 * 60 * 1000);
      
      return healthResult;

    } catch (error) {
      return {
        status: 'unhealthy',
        message: 'בעיה בגישה לנתונים השמורים',
        error: error.message,
        dataSource: 'stored'
      };
    }
  }

  /**
   * 🔍 חיפוש מוצרים בנתונים השמורים - מעודכן
   */
  async searchProducts(searchTerm, options = {}) {
    const startTime = performance.now();
    
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        throw new Error('חיפוש חייב להיות לפחות 2 תווים');
      }

      // יצירת מפתח cache
      const cacheKey = this.cache.createKey('search', {
        term: searchTerm.trim(),
        category: options.category,
        limit: options.limit || 20
      });

      // בדיקת cache
      const cached = this.cache.get(cacheKey);
      if (cached) {
        const duration = Math.round(performance.now() - startTime);
       
        return cached;
      }

      // טעינת הנתונים השמורים
      let allData;
      if (options.category) {
        // אם יש קטגוריה, מפה אותה למקור
        const sourceMapping = this.getCategoryToSourceMapping(options.category);
        if (sourceMapping) {
         
          allData = await this.loadStoredData(sourceMapping);
          // סנן לפי הקטגוריה הספציפית
          allData = this.filterByCategory(allData, options.category);
        } else {
      
          allData = await this.loadAllStoredData();
          allData = this.filterByCategory(allData, options.category);
        }
      } else if (options.source && options.source !== 'all') {
        allData = await this.loadStoredData(options.source);
      } else {
   
        allData = await this.loadAllStoredData();
      }

      // ביצוע החיפוש
      const searchTermLower = searchTerm.trim().toLowerCase();
      const results = allData.filter(item => {
        const searchableFields = [
          item.name,
          item.company,
          item.classification,
          item.id?.toString(),
          item.fundInfo?.specialization,
          item.fundInfo?.subSpecialization
        ].filter(Boolean);

        return searchableFields.some(field => 
          field.toLowerCase().includes(searchTermLower)
        );
      });

      // מיון התוצאות (העדפה לשם המדויק)
      results.sort((a, b) => {
        const aNameMatch = a.name.toLowerCase().includes(searchTermLower);
        const bNameMatch = b.name.toLowerCase().includes(searchTermLower);
        
        if (aNameMatch && !bNameMatch) return -1;
        if (!aNameMatch && bNameMatch) return 1;
        
        // מיון לפי תשואה
        return (b.returns?.year1 || 0) - (a.returns?.year1 || 0);
      });

      // הגבלת תוצאות
      const limitedResults = results.slice(0, options.limit || 20);
      
      // המרה לפורמט התצוגה
      const transformedResults = limitedResults.map(item => this.transformStoredProduct(item));

      // שמירה בcache
      this.cache.set(cacheKey, transformedResults, this.cache.CACHE_DURATION.SEARCH);
      
      const duration = Math.round(performance.now() - startTime);
  
      return transformedResults;

    } catch (error) {
      console.error('❌ Search error:', error);
      throw new Error(`שגיאה בחיפוש: ${error.message}`);
    }
  }

  /**
   * 🔍 חיפוש מהיר בקטגוריה ספציפית - חדש
   */
  async searchInCategory(searchTerm, categoryId, options = {}) {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        return [];
      }

      // קבל את נתוני הקטגוריה (מ-cache אם יש)
      const categoryData = await this.getProductsByCategory(categoryId, { limit: 1000 });
      
      // חיפוש מקומי
      const searchTermLower = searchTerm.trim().toLowerCase();
      const results = categoryData.filter(item => {
        const searchableFields = [
          item.name,
          item.company,
          item.id?.toString()
        ].filter(Boolean);

        return searchableFields.some(field => 
          field.toLowerCase().includes(searchTermLower)
        );
      });

      // מיון והגבלה
      return results
        .sort((a, b) => (b.returns?.year1 || 0) - (a.returns?.year1 || 0))
        .slice(0, options.limit || 20);

    } catch (error) {
      console.error(`❌ Error searching in category ${categoryId}:`, error);
      return [];
    }
  }

  /**
   * 📊 קבלת מוצרים לפי קטגוריה - עדכון לשימוש בפונקציה החדשה
   */
  async getProducts(categoryId, options = {}) {
    const startTime = performance.now();
    
    try {
      
      
      // יצירת מפתח cache
      const cacheKey = this.cache.createKey('category', {
        category: categoryId,
        limit: options.limit,
        sortBy: options.sortBy
      });

      // בדיקת cache
      const cached = this.cache.get(cacheKey);
      if (cached) {
        const duration = Math.round(performance.now() - startTime);
     
        return cached;
      }

      // שימוש בפונקציה החדשה המהירה
      const processedProducts = await this.getProductsByCategory(categoryId, options);

      // שמירה בcache
      this.cache.set(cacheKey, processedProducts, this.cache.CACHE_DURATION.CATEGORY);
      
      const duration = Math.round(performance.now() - startTime);
  
      
      return processedProducts;

    } catch (error) {
      console.error('❌ Error fetching products:', error);
      throw new Error(`שגיאה בקבלת מוצרים: ${error.message}`);
    }
  }

  /**
   * 🎯 קבלת מוצר ספציפי לפי ID - ללא שינוי
   */
  async getProductById(productId, category = null) {
    const startTime = performance.now();
    
    try {
    
      
      if (!productId) {
        throw new Error('חסר מזהה מוצר');
      }

      // יצירת מפתח cache
      const cacheKey = this.cache.createKey('product', {
        id: productId,
        category: category
      });

      // בדיקת cache
      const cached = this.cache.get(cacheKey);
      if (cached) {
        const duration = Math.round(performance.now() - startTime);
  
        return cached;
      }

      // טעינת נתונים
      let allData;
      if (category) {
        const sourceMapping = this.getCategoryToSourceMapping(category);
        if (sourceMapping) {
          allData = await this.loadStoredData(sourceMapping);
        } else {
          allData = await this.loadAllStoredData();
        }
      } else {
        allData = await this.loadAllStoredData();
      }

      // חיפוש המוצר
      const product = allData.find(item => 
        item.id == productId || 
        item.id === productId || 
        item.id === parseInt(productId) || 
        item.id?.toString() === productId?.toString()
      );

      if (!product) {
     
        return null;
      }

      // המרה לפורמט התצוגה
      const transformedProduct = this.transformStoredProduct(product);

      // שמירה בcache
      this.cache.set(cacheKey, transformedProduct, this.cache.CACHE_DURATION.PRODUCT);
      
      const duration = Math.round(performance.now() - startTime);

      
      return transformedProduct;

    } catch (error) {
      console.error('❌ Error in getProductById:', error);
      throw new Error(`שגיאה בטעינת המוצר: ${error.message}`);
    }
  }

  /**
   * 🔍 קבלת מוצרים דומים - ללא שינוי
   */
  async getSimilarProducts(productId, category, options = {}) {
    try {
     
      // קבל את המוצר המקורי
      const originalProduct = await this.getProductById(productId, category);
      if (!originalProduct) {
        return [];
      }

      // קבל את כל מוצרי הקטגוריה
      const allProducts = await this.getProducts(category, { limit: 200 });
      
      // סנן מוצרים דומים
      const similarProducts = allProducts
        .filter(product => product.id !== productId)
        .filter(product => {
          return product.company === originalProduct.company ||
                 product.classification === originalProduct.classification ||
                 (product.fees?.management && originalProduct.fees?.management &&
                  Math.abs(product.fees.management - originalProduct.fees.management) < 0.5);
        })
        .sort((a, b) => {
          const aDiff = Math.abs((a.returns?.year1 || 0) - (originalProduct.returns?.year1 || 0));
          const bDiff = Math.abs((b.returns?.year1 || 0) - (originalProduct.returns?.year1 || 0));
          return aDiff - bDiff;
        })
        .slice(0, options.limit || 6);

    
      return similarProducts;

    } catch (error) {
      console.error('❌ Error finding similar products:', error);
      return [];
    }
  }

  /**
   * 📈 קבלת סטטיסטיקות - ללא שינוי
   */
  async getStatistics() {
    const startTime = performance.now();
    
    try {
      const cacheKey = this.cache.createKey('stats', 'all');
      
      // בדיקת cache
      const cached = this.cache.get(cacheKey);
      if (cached) {
        const duration = Math.round(performance.now() - startTime);
       
        return cached;
      }

  

      // טעינת כל הנתונים
      const allData = await this.loadAllStoredData();

      // חישוב סטטיסטיקות
      const stats = {
        totalRecords: allData.length,
        sources: {},
        categories: {},
        avgReturns: {},
        lastUpdate: new Date().toISOString(),
        dataSource: 'stored'
      };

      // סטטיסטיקות לפי מקור
      const sourceLabels = {
        'gamal-net': 'גמל-נט',
        'pension-net': 'פנסיה-נט',
        'insurance-net': 'ביטוח-נט'
      };

      Object.keys(sourceLabels).forEach(source => {
        const sourceData = allData.filter(item => item._source === source);
        stats.sources[sourceLabels[source]] = sourceData.length;
      });

      // חישוב ממוצעי תשואות
      const validReturns = allData.filter(item => item.returns?.year1 && !isNaN(item.returns.year1));
      if (validReturns.length > 0) {
        stats.avgReturns.year1 = validReturns.reduce((sum, item) => sum + item.returns.year1, 0) / validReturns.length;
      }

      // שמירה בcache ל-30 דקות
      this.cache.set(cacheKey, stats, this.cache.CACHE_DURATION.STATISTICS);
      
      const duration = Math.round(performance.now() - startTime);
     
      return stats;

    } catch (error) {
      console.error('❌ Error calculating statistics:', error);
      throw new Error(`שגיאה בחישוב סטטיסטיקות: ${error.message}`);
    }
  }

  /**
   * 🏷️ מיפוי קטגוריה למקור - ללא שינוי
   */
 getCategoryToSourceMapping(categoryId) {
  const mapping = {
    'study-funds': 'gamal-net',
    'pension-savings': 'gamal-net', 
    'pension-funds': 'gamal-net',
    'investment-funds': 'gamal-net',
    'child-savings': 'gamal-net',
    'pension-plans': 'pension-net',
    'insurance-savings': 'insurance-net',
    'insurance-management': 'insurance-net' // הוסף את השורה הזו
  };
  return mapping[categoryId];
}

  /**
   * 🔧 סינון לפי קטגוריה - ללא שינוי
   */
  filterByCategory(allData, categoryId) {
    // אם לא צוינה קטגוריה, החזר הכל
    if (!categoryId || categoryId === 'all') {
      return allData;
    }

    return allData.filter(item => {
      const classification = item.classification || '';
      
      switch (categoryId) {
        case 'study-funds':
          return classification.includes('השתלמות');
          
      case 'pension-savings':
case 'pension-funds':
  return classification.includes('תגמולים') || 
         classification.includes('פיצויים') || 
         classification.includes('מטרה אחרת');
        case 'investment-funds':
          return classification.includes('גמל להשקעה') && !classification.includes('ילד');
          
        case 'child-savings':
          return classification.includes('גמל להשקעה') && classification.includes('ילד');
          
  case 'pension-plans':
  // אם יש שדה pensionProductType, השתמש בו
  if (item.pensionProductType) {
    return true; // כל הקרנות בפנסיה-נט הן קרנות פנסיה
  }
  // אחרת השתמש בקריטריונים הישנים
  return classification.includes('קרנות חדשות') || 
         classification.includes('קרנות כלליות');
case 'insurance-management':
  return item.isManagementFund === true; // לא return allData.filter()!

case 'insurance-savings':
  return item.isSavingsPolicy === true;
        default:
          return true;
      }
    });
  }

  /**
   * 🔄 עיבוד מוצרים שמורים - ללא שינוי
   */
  processStoredProducts(rawData, options) {
    let products = rawData.map(item => this.transformStoredProduct(item));
    
    if (options.sortBy) {
      products = this.sortProducts(products, options.sortBy);
    }
    
    if (options.limit) {
      products = products.slice(0, options.limit);
    }
    
    return products;
  }

// עדכון לפונקציית transformStoredProduct בתוך OptimizedLocalAPI

/**
 * 🔄 המרת מוצר שמור לפורמט התצוגה - מעודכן עם סטטוס פעילות
 */
transformStoredProduct(rawProduct) {
  const safeParseFloat = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  const safeParseBool = (value) => {
    if (value === null || value === undefined || value === '') return false;
    return Boolean(value);
  };

  return {
    id: rawProduct.id?.toString() || '',
    name: rawProduct.name || 'שם לא זמין',
    company: rawProduct.company || 'חברה לא ידועה',
    classification: rawProduct.classification || '',
        // 🆕 הוסף את השדות האלה
    isSavingsPolicy: safeParseBool(rawProduct.isSavingsPolicy),
    isManagementFund: safeParseBool(rawProduct.isManagementFund),
    insuranceProductType: rawProduct.insuranceProductType || null,
    // 🆕 שדות פנסיה משלימה/מקיפה
isComplementaryPension: safeParseBool(rawProduct.isComplementaryPension),
isComprehensivePension: safeParseBool(rawProduct.isComprehensivePension),
pensionProductType: rawProduct.pensionProductType || null,
    source: rawProduct._source || rawProduct.source || 'unknown',
    sourceLabel: rawProduct._sourceLabel || this.getSourceLabel(rawProduct._source || rawProduct.source) || 'לא ידוע',
    
    // 🆕 מידע על פעילות הקופה
    isActive: safeParseBool(rawProduct.isActive),
    activityStatus: rawProduct.activityStatus || (rawProduct.isActive ? 'פעילה' : 'לא פעילה'),
    activityStatusEn: rawProduct.activityStatusEn || (rawProduct.isActive ? 'active' : 'inactive'),
    activityReason: rawProduct.activityReason || 'לא ידוע',
    activityDetails: rawProduct.activityDetails ? {
      hasAssets: safeParseBool(rawProduct.activityDetails.hasAssets),
      hasDeposits: safeParseBool(rawProduct.activityDetails.hasDeposits),
      hasWithdrawals: safeParseBool(rawProduct.activityDetails.hasWithdrawals),
      hasNetDeposits: safeParseBool(rawProduct.activityDetails.hasNetDeposits),
      hasInternalTransfers: safeParseBool(rawProduct.activityDetails.hasInternalTransfers),
      totalAssetsAmount: safeParseFloat(rawProduct.activityDetails.totalAssetsAmount) || 0,
      lastActivityIndicators: {
        deposits: safeParseFloat(rawProduct.activityDetails.lastActivityIndicators?.deposits) || 0,
        withdrawals: safeParseFloat(rawProduct.activityDetails.lastActivityIndicators?.withdrawals) || 0,
        netMonthlyDeposits: safeParseFloat(rawProduct.activityDetails.lastActivityIndicators?.netMonthlyDeposits) || 0,
        internalTransfers: safeParseFloat(rawProduct.activityDetails.lastActivityIndicators?.internalTransfers) || 0
      }
    } : null,
    
    // תשואות - כולל שנתיים
    returns: {
      month1: safeParseFloat(rawProduct.returns?.month1),
      ytd: safeParseFloat(rawProduct.returns?.ytd),
      year1: safeParseFloat(rawProduct.returns?.year1),
      year2: safeParseFloat(rawProduct.returns?.year2),
      year4: safeParseFloat(rawProduct.returns?.year4),
      year3: safeParseFloat(rawProduct.returns?.year3),
      year5: safeParseFloat(rawProduct.returns?.year5),
      avgAnnual2Years: safeParseFloat(rawProduct.returns?.avgAnnual2Years),
      avgAnnual3Years: safeParseFloat(rawProduct.returns?.avgAnnual3Years),
      avgAnnual4Years: safeParseFloat(rawProduct.returns?.avgAnnual4Years),
      avgAnnual5Years: safeParseFloat(rawProduct.returns?.avgAnnual5Years)
    },
    
    riskLevel: rawProduct.riskLevel ? {
      level: rawProduct.riskLevel.level || 0,
      stars: rawProduct.riskLevel.stars || '❓',
      color: rawProduct.riskLevel.color || 'text-gray-600',
      bgColor: rawProduct.riskLevel.bgColor || 'bg-gray-50 border-gray-200',
      name: rawProduct.riskLevel.name || 'לא ידוע',
      description: rawProduct.riskLevel.description || 'אין נתוני חשיפה זמינים'
    } : {
      level: 0,
      stars: '❓',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50 border-gray-200',
      name: 'לא ידוע',
      description: 'אין נתוני רמת סיכון זמינים'
    },
    
    fees: {
      management: safeParseFloat(rawProduct.fees?.management),
      deposit: safeParseFloat(rawProduct.fees?.deposit)
    },
    
    performance: {
      standardDeviation: safeParseFloat(rawProduct.performance?.standardDeviation),
      alpha: safeParseFloat(rawProduct.performance?.alpha),
      sharpeRatio: safeParseFloat(rawProduct.performance?.sharpeRatio),
      liquidAssetsPercent: safeParseFloat(rawProduct.performance?.liquidAssetsPercent)
    },
    
    totalAssets: rawProduct.totalAssets || rawProduct.financialData?.totalAssets || 0,
    
    // הנתונים החסרים החשובים
    deposits: safeParseFloat(rawProduct.deposits),
    withdrawals: safeParseFloat(rawProduct.withdrawals),
    internalTransfers: safeParseFloat(rawProduct.internalTransfers),
    netMonthlyDeposits: safeParseFloat(rawProduct.netMonthlyDeposits),
    actuarialAdjustment: safeParseFloat(rawProduct.actuarialAdjustment),
    
    exposures: {
      stocks: safeParseFloat(rawProduct.exposures?.stocks),
      foreign: safeParseFloat(rawProduct.exposures?.foreign),
      currency: safeParseFloat(rawProduct.exposures?.currency)
    },
    
    fundInfo: {
      inceptionDate: rawProduct.fundInfo?.inceptionDate || rawProduct.inceptionDate,
      targetPopulation: rawProduct.fundInfo?.targetPopulation || rawProduct.targetPopulation || 'כלל האוכלוסיה',
      specialization: rawProduct.fundInfo?.specialization || rawProduct.specialization || '',
      subSpecialization: rawProduct.fundInfo?.subSpecialization || rawProduct.subSpecialization || '',
      controllingCorp: rawProduct.fundInfo?.controllingCorp || rawProduct.controllingCorp || '',
      managingCorp: rawProduct.fundInfo?.managingCorp || rawProduct.managingCorp || ''
    },
    
    managingCorpLegalId: rawProduct.managingCorpLegalId || 
                        rawProduct.rawData?.MANAGING_CORPORATION_LEGAL_ID?.toString() ||
                        null,
                        
    reportPeriod: rawProduct.reportPeriod?.toString() || rawProduct.technical?.reportPeriod || '',
    currentDate: rawProduct.currentDate || null,
    
    dataQuality: rawProduct.dataQuality ? {
      has12MonthsData: rawProduct.dataQuality.has12MonthsData || false,
      has24MonthsData: rawProduct.dataQuality.has24MonthsData || false,
      has48MonthsData: rawProduct.dataQuality.has48MonthsData || false,
      hasRiskData: rawProduct.dataQuality.hasRiskData || false,
      calculationMethod: rawProduct.dataQuality.calculationMethod || 'unknown',
      fundActiveStatus: safeParseBool(rawProduct.dataQuality.fundActiveStatus) // 🆕 הוסף גם כאן
    } : {
      has12MonthsData: false,
      has24MonthsData: false,
      has48MonthsData: false,
      hasRiskData: false,
      calculationMethod: 'legacy',
      fundActiveStatus: false // 🆕
    },
    
    isRealData: true,
    lastUpdate: rawProduct.lastUpdate || rawProduct.technical?.lastUpdate || new Date().toISOString(),
    loadedFrom: 'stored'
  };
}

// 🆕 פונקציות עזר חדשות ל-API Service

/**
 * 🔍 סינון קופות לפי סטטוס פעילות
 */
async getProductsByActivityStatus(categoryId, isActive = true, options = {}) {
  try {
    console.log(`🔍 Loading ${isActive ? 'active' : 'inactive'} products for category: ${categoryId}`);
    
    // קבל את כל מוצרי הקטגוריה
    const allProducts = await this.getProductsByCategory(categoryId, { limit: 5000 });
    
    // סנן לפי סטטוס פעילות
    const filteredProducts = allProducts.filter(product => product.isActive === isActive);
    
    // מיון והגבלה
    let results = filteredProducts;
    if (options.sortBy) {
      results = this.sortProducts(results, options.sortBy);
    }
    if (options.limit) {
      results = results.slice(0, options.limit);
    }
    
    console.log(`✅ Found ${filteredProducts.length} ${isActive ? 'active' : 'inactive'} products`);
    return results;
    
  } catch (error) {
    console.error(`❌ Error getting ${isActive ? 'active' : 'inactive'} products:`, error);
    return [];
  }
}

/**
 * 📊 קבלת סטטיסטיקות פעילות לפי קטגוריה
 */
async getActivityStatsByCategory(categoryId) {
  try {
    console.log(`📊 Getting activity stats for category: ${categoryId}`);
    
    const allProducts = await this.getProductsByCategory(categoryId, { limit: 5000 });
    
    const stats = {
      total: allProducts.length,
      active: allProducts.filter(p => p.isActive === true).length,
      inactive: allProducts.filter(p => p.isActive === false).length,
      unknown: allProducts.filter(p => p.isActive === null || p.isActive === undefined).length
    };
    
    stats.activePercentage = stats.total > 0 ? ((stats.active / stats.total) * 100).toFixed(1) : 0;
    stats.inactivePercentage = stats.total > 0 ? ((stats.inactive / stats.total) * 100).toFixed(1) : 0;
    
    // קיבוץ לפי סיבות אי-פעילות
    const inactiveProducts = allProducts.filter(p => p.isActive === false);
    const inactivityReasons = {};
    
    inactiveProducts.forEach(product => {
      const reason = product.activityReason || 'לא ידוע';
      if (!inactivityReasons[reason]) {
        inactivityReasons[reason] = 0;
      }
      inactivityReasons[reason]++;
    });
    
    stats.inactivityReasons = inactivityReasons;
    
    console.log(`📊 Activity stats:`, stats);
    return stats;
    
  } catch (error) {
    console.error('❌ Error getting activity stats:', error);
    return {
      total: 0,
      active: 0,
      inactive: 0,
      unknown: 0,
      activePercentage: 0,
      inactivePercentage: 0,
      inactivityReasons: {}
    };
  }
}


// הוסף את הפונקציה הזו ל-api.js
/**
 * 🎯 קבלת כל ה-classifications הקיימים בקטגוריה ספציפית
 */
async getClassificationsByCategory(categoryId) {
  const startTime = performance.now();
  
  try {
    console.log(`🔍 Getting classifications for category: ${categoryId}`);
    
    // יצירת מפתח cache
    const cacheKey = this.cache.createKey('classifications', {
      category: categoryId
    });

    // בדיקת cache
    const cached = this.cache.get(cacheKey);
    if (cached) {
      const duration = Math.round(performance.now() - startTime);
      console.log(`✅ Classifications cache HIT for ${categoryId} (${duration}ms):`, cached);
      return cached;
    }

    // טען את כל המוצרים מהקטגוריה
    const allProducts = await this.getProductsByCategory(categoryId, { limit: 5000 });
    
    // חלץ את כל ה-classifications הייחודיים
    const classifications = [...new Set(allProducts.map(p => p.classification))]
      .filter(Boolean)
      .sort();
    
    // שמירה בcache ל-15 דקות
    this.cache.set(cacheKey, classifications, 15 * 60 * 1000);
    
    const duration = Math.round(performance.now() - startTime);
    console.log(`✅ Found ${classifications.length} classifications in ${categoryId} (${duration}ms):`, classifications);
    
    return classifications;
    
  } catch (error) {
    console.error(`❌ Error getting classifications for ${categoryId}:`, error);
    return [];
  }
}

/**
 * 🎯 מציאת קופות לא פעילות עם נכסים
 */
async findInactiveWithAssets(categoryId, minAssets = 1000000) {
  try {
    const inactiveProducts = await this.getProductsByActivityStatus(categoryId, false);
    
    const withAssets = inactiveProducts.filter(product => 
      product.totalAssets > minAssets &&
      product.activityDetails?.hasAssets === true
    );
    
    // מיון לפי גודל נכסים
    withAssets.sort((a, b) => b.totalAssets - a.totalAssets);
    
    console.log(`🎯 Found ${withAssets.length} inactive funds with assets > ${minAssets.toLocaleString()}`);
    return withAssets;
    
  } catch (error) {
    console.error('❌ Error finding inactive funds with assets:', error);
    return [];
  }
}

/**
 * 📈 השוואת ביצועי קופות פעילות מול לא פעילות
 */
async compareActiveVsInactivePerformance(categoryId) {
  try {
    const allProducts = await this.getProductsByCategory(categoryId, { limit: 5000 });
    
    const activeProducts = allProducts.filter(p => p.isActive === true && p.returns?.year1);
    const inactiveProducts = allProducts.filter(p => p.isActive === false && p.returns?.year1);
    
    const calculateAvgReturn = (products) => {
      if (products.length === 0) return 0;
      const sum = products.reduce((acc, p) => acc + (p.returns?.year1 || 0), 0);
      return sum / products.length;
    };
    
    const comparison = {
      active: {
        count: activeProducts.length,
        avgReturn: calculateAvgReturn(activeProducts),
        totalAssets: activeProducts.reduce((sum, p) => sum + (p.totalAssets || 0), 0)
      },
      inactive: {
        count: inactiveProducts.length,
        avgReturn: calculateAvgReturn(inactiveProducts),
        totalAssets: inactiveProducts.reduce((sum, p) => sum + (p.totalAssets || 0), 0)
      }
    };
    
    comparison.performanceDiff = comparison.active.avgReturn - comparison.inactive.avgReturn;
    
    console.log(`📈 Performance comparison:`, comparison);
    return comparison;
    
  } catch (error) {
    console.error('❌ Error comparing performance:', error);
    return null;
  }
}

// עדכון לפונקציית המיון - הוסף אפשרויות מיון חדשות
// const updatedSortFunctions = {
//   // הוסף למיון הקיים:
//   'activityStatus': (a, b) => {
//     if (a.isActive === b.isActive) return 0;
//     return a.isActive ? -1 : 1; // פעילות קודם
//   },
//   'totalAssets': (a, b) => (b.totalAssets || 0) - (a.totalAssets || 0),
//   'deposits': (a, b) => (b.deposits || 0) - (a.deposits || 0),
//   'withdrawals': (a, b) => (b.withdrawals || 0) - (a.withdrawals || 0),
//   'netDeposits': (a, b) => (b.netMonthlyDeposits || 0) - (a.netMonthlyDeposits || 0)
// };

  /**
   * 📊 מיון מוצרים - ללא שינוי
   */
/**
   * 📊 מיון מוצרים - מעודכן עם שדות חדשים
   */
  sortProducts(products, sortBy) {
    const sortFunctions = {
      // תשואות מצטברות
      'year1': (a, b) => (b.returns?.year1 || 0) - (a.returns?.year1 || 0),
      'year2': (a, b) => (b.returns?.year2 || 0) - (a.returns?.year2 || 0),        // 🆕 24 חודשים
      'year3': (a, b) => (b.returns?.year3 || 0) - (a.returns?.year3 || 0),
      'year4': (a, b) => (b.returns?.year4 || 0) - (a.returns?.year4 || 0),        // 🆕 48 חודשים
      'year5': (a, b) => (b.returns?.year5 || 0) - (a.returns?.year5 || 0),
      
      // 🆕 ממוצעים שנתיים
      'avgAnnual2Years': (a, b) => (b.returns?.avgAnnual2Years || 0) - (a.returns?.avgAnnual2Years || 0),
      'avgAnnual3Years': (a, b) => (b.returns?.avgAnnual3Years || 0) - (a.returns?.avgAnnual3Years || 0),
      'avgAnnual4Years': (a, b) => (b.returns?.avgAnnual4Years || 0) - (a.returns?.avgAnnual4Years || 0),
      'avgAnnual5Years': (a, b) => (b.returns?.avgAnnual5Years || 0) - (a.returns?.avgAnnual5Years || 0),
      
      // 🆕 רמת סיכון
      'riskLevel': (a, b) => (a.riskLevel?.level || 0) - (b.riskLevel?.level || 0),    // סיכון נמוך לגבוה
      'riskLevelDesc': (a, b) => (b.riskLevel?.level || 0) - (a.riskLevel?.level || 0), // סיכון גבוה לנמוך
      
      // תשואות קצרות
      'month1': (a, b) => (b.returns?.month1 || 0) - (a.returns?.month1 || 0),
      'ytd': (a, b) => (b.returns?.ytd || 0) - (a.returns?.ytd || 0),
      
      // עלויות וביצועים
      'fee': (a, b) => (a.fees?.management || 0) - (b.fees?.management || 0),
      'assets': (a, b) => (b.totalAssets || 0) - (a.totalAssets || 0),
      'deposits': (a, b) => (b.deposits || 0) - (a.deposits || 0),                   // 🆕
      'sharpe': (a, b) => (b.performance?.sharpeRatio || 0) - (a.performance?.sharpeRatio || 0), // 🆕
      
      // חשיפות
      'stocksExposure': (a, b) => (b.exposures?.stocks || 0) - (a.exposures?.stocks || 0), // 🆕
      'foreignExposure': (a, b) => (b.exposures?.foreign || 0) - (a.exposures?.foreign || 0), // 🆕
      
      // כללי
      'name': (a, b) => a.name.localeCompare(b.name, 'he'),
      'company': (a, b) => a.company.localeCompare(b.company, 'he')                 // 🆕
    };

    const sortFunction = sortFunctions[sortBy] || sortFunctions['year1'];
    return [...products].sort(sortFunction);
  }

  /**
   * 🏷️ קבלת תווית מקור - ללא שינוי
   */
getSourceLabel(sourceKey) {
  const labels = {
    'gamal-net': 'גמל-נט',
    'pension-net': 'פנסיה-נט',
    'insurance-net': 'ביטוח-נט',
    'insurance-savings': 'פוליסות חיסכון', // הוסף
    'insurance-management': 'ביטוח מנהלים' // הוסף
  };
  return labels[sourceKey] || sourceKey;
}
  /**
   * 🔄 רענון נתונים - ללא שינוי
   */
  async refreshData() {
   
    
    // ניקוי cache מקומי
    this.localData.clear();
    this.cache.clear();
    this.pendingRequests.clear();
    
    // 🆕 ניקוי cache הקטגוריות החדש
    this.categoryCache.clear();
    
    // טעינה מחדש של הנתונים
    await this.loadAllStoredData();
    
    
  }

  /**
   * 🧹 ניקוי Cache - מעודכן
   */
clearCache() {
  this.cache.clear();
  this.localData.clear();
  this.pendingRequests.clear();
  this.categoryCache.clear();
  this.tableCache.clear(); // 🚀 הוסף את זה
  console.log('🧹 All caches cleared');
}

  /**
   * 🧹 ניקוי Cache עם TTL - חדש
   */
  clearExpiredCache() {
    const now = Date.now();
    
    for (const [key, value] of this.categoryCache.entries()) {
      if (now - value.timestamp > this.categoryTTL) {
        this.categoryCache.delete(key);
      
      }
    }
  }

  /**
   * 🔄 מעקב אחר מעגל רענון Cache - חדש
   */
  startCacheRefreshLoop() {
    // ניקוי cache כל 5 דקות
    setInterval(() => {
      this.clearExpiredCache();
    }, 5 * 60 * 1000);

 
  }

  /**
   * 📊 סטטיסטיקות Cache - מעודכן
   */
  getCacheStats() {
    return {
      ...this.cache.getStats(),
      localDataSize: this.localData.size,
      categoryCache: this.categoryCache.size, // 🆕
      pendingRequests: this.pendingRequests.size
    };
  }

  /**
   * 📈 מעקב אחר ביצועים - חדש
   */
  getPerformanceMetrics() {
    const stats = this.getCacheStats();
    
    return {
      cacheStats: stats,
      categoryTTL: this.categoryTTL,
      preloadedCategories: Array.from(this.categoryCache.keys()),
      lastCacheCleanup: new Date().toISOString()
    };
  }

  async getAiScoreIndex({ source = 'all', forceRefresh = false } = {}) {
    const cacheKey = this.cache.createKey('aiScoreIndex', { source });

    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const url = new URL(`${this.baseUrl}/getAiScoreIndex`);
    if (source && source !== 'all') {
      url.searchParams.set('source', source);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      timeout: API_CONFIG.timeout
    });

    if (!response.ok) {
      throw new Error(`Failed to load AI score index (${response.status})`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'AI score index request failed');
    }

    const payload = result.sources || {};
    this.cache.set(cacheKey, payload, 5 * 60 * 1000);
    return payload;
  }

  async getAiScoreTrack({ source, trackId, limit, forceRefresh = false }) {
    if (!source) {
      throw new Error('source is required to load AI score track');
    }
    if (!trackId) {
      throw new Error('trackId is required to load AI score track');
    }

    const cacheKey = this.cache.createKey('aiScoreTrack', {
      source,
      trackId,
      limit: limit || null
    });

    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const url = new URL(`${this.baseUrl}/getAiScoreTrack`);
    url.searchParams.set('source', source);
    url.searchParams.set('trackId', trackId);
    if (limit) {
      url.searchParams.set('limit', `${limit}`);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      timeout: API_CONFIG.timeout
    });

    if (!response.ok) {
      throw new Error(`Failed to load AI score track (${response.status})`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'AI score track request failed');
    }

    const payload = result.track || result;
    this.cache.set(cacheKey, payload, 5 * 60 * 1000);
    return payload;
  }

  async getAiScoreForFund({ fundId, source, productType, forceRefresh = false }) {
    if (!fundId) {
      throw new Error('fundId is required to look up AI score');
    }

    const cacheKey = this.cache.createKey('aiScoreFund', {
      fundId,
      source: source || 'all',
      productType: productType || 'all'
    });

    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    const url = new URL(`${this.baseUrl}/getAiScoreForFund`);
    url.searchParams.set('fundId', `${fundId}`);
    if (source) {
      url.searchParams.set('source', source);
    }
    if (productType) {
      url.searchParams.set('productType', productType);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      timeout: API_CONFIG.timeout
    });

    if (!response.ok) {
      if (response.status === 404) {
        this.cache.set(cacheKey, [], 60 * 1000);
        return [];
      }
      throw new Error(`Failed to load AI score for fund (${response.status})`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'AI score fund request failed');
    }

    const payload = result.matches || [];
    this.cache.set(cacheKey, payload, 5 * 60 * 1000);
    return payload;
  }
}

// יצירת instance יחיד עם שהפעלת לולאת רענון
const api = new OptimizedLocalAPI();
api.startCacheRefreshLoop();

export default api;